import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle, Phone, Mail, MapPin, Navigation, Upload, CheckCircle2, Loader2, ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import PageHeader from "../components/sections/PageHeader";
import { Eyebrow } from "../components/CTA";
import { COMPANY, buildWhatsAppLink } from "../lib/site";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactCard = ({ icon: Icon, label, children, testId }) => (
  <div className="rounded-2xl border border-navy/10 bg-white p-6 transition-shadow duration-300 hover:shadow-[0_16px_40px_rgba(10,17,40,0.06)]" data-testid={testId}>
    <div className="flex items-center gap-3">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white">
        <Icon className="h-5 w-5" />
      </span>
      <span className="text-xs font-bold uppercase tracking-[0.18em] text-steel">{label}</span>
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

const Inquiry = () => {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [drawingName, setDrawingName] = useState("");
  const [form, setForm] = useState({
    full_name: "", company_name: "", phone: "", email: "",
    product_requirement: "", quantity: "", message: "",
  });

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone and email.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      const fileInput = document.getElementById("drawing-input");
      if (fileInput && fileInput.files[0]) fd.append("drawing", fileInput.files[0]);

      await axios.post(`${API}/inquiries`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Forward the inquiry directly to WhatsApp with a pre-filled summary
      const waMsg =
        `New Inquiry — PAHI TECHNOCRATE PVT. LTD.%0A%0A` +
        `Name: ${form.full_name}%0A` +
        `Company: ${form.company_name || "-"}%0A` +
        `Phone: ${form.phone}%0A` +
        `Email: ${form.email}%0A` +
        `Product: ${form.product_requirement || "-"}%0A` +
        `Quantity: ${form.quantity || "-"}%0A` +
        `Message: ${form.message || "-"}`;
      const waUrl = `https://wa.me/${COMPANY.whatsapp}?text=${waMsg}`;

      setDone(true);
      window.open(waUrl, "_blank", "noopener,noreferrer");
      toast.success("Inquiry submitted — opening WhatsApp.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or WhatsApp us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="inquiry-page">
      <Seo
        title="Contact & Request a Quote"
        description="Contact PAHI TECHNOCRATE PVT. LTD. in Neemrana, Rajasthan. WhatsApp +91 98298 03123, call +91 98298 03123 / +91 92612 00890, email ajay@pahi.co.in. Request a quote or upload your drawing — we respond within 24 hours."
        path="/inquiry"
        schema={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact PAHI TECHNOCRATE PVT. LTD.",
          url: "https://pahi.co.in/inquiry",
        }}
      />
      <PageHeader
        eyebrow="Get in Touch"
        crumb="Contact"
        titleLines={["Let's discuss your", <span key="i" className="text-brand-soft">manufacturing requirement.</span>]}
        subtitle="Share your drawings and specifications — our engineering team responds within 24 hours."
      />

      <section className="bg-surface py-20 sm:py-28">
        <div className="mx-auto grid max-w-[1400px] gap-10 px-5 sm:px-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
          {/* LEFT — contact details */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <Eyebrow>Contact Details</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-navy">
              Reach our team directly.
            </h2>

            <div className="mt-8 space-y-4">
              <ContactCard icon={MessageCircle} label="WhatsApp" testId="contact-whatsapp">
                <div className="font-display text-lg font-semibold text-navy">{COMPANY.whatsappDisplay}</div>
                <a
                  href={buildWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-chat-btn"
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-transform duration-300 hover:scale-[1.03]"
                >
                  <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
                </a>
              </ContactCard>

              <ContactCard icon={Phone} label="Phone" testId="contact-phone">
                <div className="space-y-1">
                  {COMPANY.phones.map((p) => (
                    <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="block font-display text-lg font-semibold text-navy hover:text-brand">
                      {p}
                    </a>
                  ))}
                </div>
              </ContactCard>

              <ContactCard icon={Mail} label="Email" testId="contact-email">
                <div className="space-y-1">
                  {COMPANY.emails.map((em) => (
                    <a key={em} href={`mailto:${em}`} className="block font-display text-base font-semibold text-navy hover:text-brand">
                      {em}
                    </a>
                  ))}
                </div>
              </ContactCard>

              <ContactCard icon={MapPin} label="Address" testId="contact-address">
                <p className="text-sm leading-relaxed text-slate-600">{COMPANY.address.full}</p>
                <div className="mt-4 overflow-hidden rounded-xl border border-navy/10">
                  <iframe
                    title="PAHI Location"
                    src={COMPANY.mapEmbed}
                    className="h-48 w-full"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <a
                  href={COMPANY.mapDirections}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="get-directions-btn"
                  className="mt-4 inline-flex items-center gap-2 rounded-full border border-navy/20 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-navy transition-colors duration-300 hover:bg-navy hover:text-white"
                >
                  <Navigation className="h-4 w-4" /> Get Directions
                </a>
              </ContactCard>
            </div>
          </div>

          {/* RIGHT — form */}
          <div className="relative rounded-3xl border border-navy/10 bg-white p-7 sm:p-10">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex min-h-[420px] flex-col items-center justify-center text-center"
                  data-testid="inquiry-success"
                >
                  <span className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-brand/10 text-brand">
                    <CheckCircle2 className="h-10 w-10" />
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-semibold text-navy">
                    Thank you for contacting PAHI TECHNOCRATE PVT. LTD.
                  </h3>
                  <p className="mt-3 max-w-md text-slate-500">
                    Our team will contact you within 24 hours. If WhatsApp did not open automatically, tap below.
                  </p>
                  <a
                    href={buildWhatsAppLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-white"
                  >
                    <MessageCircle className="h-4 w-4" /> Open WhatsApp
                  </a>
                  <button
                    onClick={() => { setDone(false); setForm({ full_name: "", company_name: "", phone: "", email: "", product_requirement: "", quantity: "", message: "" }); setDrawingName(""); }}
                    data-testid="new-inquiry-btn"
                    className="mt-4 text-sm font-medium text-navy/60 underline underline-offset-4 hover:text-navy"
                  >
                    Submit another inquiry
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-5"
                  data-testid="inquiry-form"
                >
                  <div>
                    <Eyebrow>Request a Quote</Eyebrow>
                    <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-navy">
                      Tell us what you need.
                    </h2>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Full Name *" name="full_name" value={form.full_name} onChange={onChange} placeholder="Your name" testId="input-full-name" />
                    <Field label="Company Name" name="company_name" value={form.company_name} onChange={onChange} placeholder="Company" testId="input-company" />
                    <Field label="Phone *" name="phone" value={form.phone} onChange={onChange} placeholder="+91 ..." testId="input-phone" />
                    <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} placeholder="you@company.com" testId="input-email" />
                    <Field label="Product Requirement" name="product_requirement" value={form.product_requirement} onChange={onChange} placeholder="e.g. Laser cut enclosures" testId="input-product" />
                    <Field label="Quantity" name="quantity" value={form.quantity} onChange={onChange} placeholder="e.g. 5,000 pcs / month" testId="input-quantity" />
                  </div>

                  <div>
                    <Label className="text-xs font-bold uppercase tracking-[0.14em] text-steel">Message</Label>
                    <Textarea
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      placeholder="Describe your project, tolerances, materials and timelines..."
                      rows={4}
                      className="mt-2 rounded-xl border-navy/15 focus-visible:ring-brand"
                      data-testid="input-message"
                    />
                  </div>

                  {/* Drawing upload */}
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-[0.14em] text-steel">Drawing Upload</Label>
                    <label
                      htmlFor="drawing-input"
                      className="mt-2 flex cursor-pointer items-center justify-between rounded-xl border border-dashed border-navy/25 bg-surface px-5 py-4 transition-colors duration-300 hover:border-brand"
                      data-testid="drawing-upload"
                    >
                      <span className="flex items-center gap-3 text-sm text-slate-500">
                        <Upload className="h-5 w-5 text-navy" />
                        {drawingName || "Upload drawing (PDF, DWG, PNG, JPG)"}
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-wide text-brand">Browse</span>
                    </label>
                    <input
                      id="drawing-input"
                      type="file"
                      accept=".pdf,.dwg,.dxf,.step,.stp,.png,.jpg,.jpeg,.zip"
                      className="hidden"
                      onChange={(e) => setDrawingName(e.target.files[0]?.name || "")}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="submit-inquiry-btn"
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-brand disabled:opacity-60"
                  >
                    {submitting ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</>
                    ) : (
                      <>Submit Inquiry <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>
                    )}
                  </button>
                  <p className="text-center text-xs text-slate-400">
                    Your inquiry is stored securely and forwarded to our team via WhatsApp.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, value, onChange, placeholder, type = "text", testId }) => (
  <div>
    <Label className="text-xs font-bold uppercase tracking-[0.14em] text-steel">{label}</Label>
    <Input
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="mt-2 h-12 rounded-xl border-navy/15 focus-visible:ring-brand"
      data-testid={testId}
    />
  </div>
);

export default Inquiry;
